import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { functions, db } from '../firebase.js';
import { currentUser } from './auth.js';
import { logClientError } from './errorLog.js';

// ─────────────────────────────────────────────────────────────────────────
// Quiz score history — client half. Generated pages report a completed
// quiz attempt to their parent via `postMessage({ __llQuizScore: true,
// score, maxScore }, '*')` (see functions/systemPrompt.js's SCORE REPORTING
// instruction). attachQuizScoreBridge listens for that message on every
// iframe a real learner is looking at (the same set of iframes
// attachAskAIBridge is wired to — see src/lib/askAI.js) and persists it via
// the recordQuizScore Cloud Function, which is the only thing allowed to
// write users/{uid}/quizAttempts (firestore.rules denies direct writes).
// ─────────────────────────────────────────────────────────────────────────

const recordQuizScoreFn = httpsCallable(functions, 'recordQuizScore');
const getStudentQuizHistoryFn = httpsCallable(functions, 'getStudentQuizHistory');
const recordSchoolSubscriptionQuizScoreFn = httpsCallable(functions, 'recordSchoolSubscriptionQuizScore');
const getSchoolQuizLogFn = httpsCallable(functions, 'getSchoolQuizLog');
const recordPurchaseQuizScoreFn = httpsCallable(functions, 'recordPurchaseQuizScore');

// Wire once per live iframe, alongside attachAskAIBridge — see its own doc
// comment in src/lib/askAI.js for exactly which call sites that means.
// courseId/topicId come from the PARENT's own context, never trusted from
// the iframe (which could claim to be any topic). Returns a detach()
// function, same convention as attachAskAIBridge.
export function attachQuizScoreBridge(iframeEl, { courseId, topicId }) {
  async function onMessage(event) {
    const d = event.data;
    if (!d || d.__llQuizScore !== true || event.source !== iframeEl.contentWindow) return;
    const score = Number(d.score);
    const maxScore = Number(d.maxScore);
    if (!Number.isFinite(score) || !Number.isFinite(maxScore)) return;
    try {
      await recordQuizScoreFn({ courseId, topicId, score, maxScore });
    } catch (err) {
      // Silent to the learner — a failed history write shouldn't interrupt
      // their quiz result, which is already shown on the page itself.
      logClientError({ context: 'recordQuizScore', error: err, displayMessage: '', courseId, topicId });
    }
  }
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

// Same idea as attachQuizScoreBridge, for a topic viewed through a school
// subscription's read-through (src/views/home.js openSchoolSubscriptionViewer)
// rather than the viewer's own course tree. listingId/topicId come from the
// PARENT's own context (the subscription content the school already fetched
// — see lib/schoolMarketplace.js getSchoolSubscriptionContent), never from
// the iframe. The server re-derives who actually owns this content and
// attributes the attempt to them (functions/schoolMarketplace.js
// recordSchoolSubscriptionQuizScore) — this also lands in the viewer's own
// "My Scores", same as attachQuizScoreBridge's writes.
export function attachSchoolSubscriptionQuizScoreBridge(iframeEl, { listingId, topicId }) {
  async function onMessage(event) {
    const d = event.data;
    if (!d || d.__llQuizScore !== true || event.source !== iframeEl.contentWindow) return;
    const score = Number(d.score);
    const maxScore = Number(d.maxScore);
    if (!Number.isFinite(score) || !Number.isFinite(maxScore)) return;
    try {
      await recordSchoolSubscriptionQuizScoreFn({ listingId, topicId, score, maxScore });
    } catch (err) {
      logClientError({ context: 'recordSchoolSubscriptionQuizScore', error: err, displayMessage: '', extra: { listingId, topicId } });
    }
  }
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

// Same idea again, for a topic viewed through an individually purchased
// (view-only) marketplace listing (src/views/marketplace.js
// openPurchaseViewer) — the one remaining content viewer that previously had
// no score-reporting bridge at all, so a completed quiz there was never
// recorded for anyone. purchaseId/topicId come from the PARENT's own
// context, never from the iframe; the server re-derives the creator/course
// from the caller's own purchase record (functions/marketplace.js
// recordPurchaseQuizScore) — this also lands in the viewer's own
// "My Scores", same as attachQuizScoreBridge's writes.
export function attachPurchaseQuizScoreBridge(iframeEl, { purchaseId, topicId }) {
  async function onMessage(event) {
    const d = event.data;
    if (!d || d.__llQuizScore !== true || event.source !== iframeEl.contentWindow) return;
    const score = Number(d.score);
    const maxScore = Number(d.maxScore);
    if (!Number.isFinite(score) || !Number.isFinite(maxScore)) return;
    try {
      await recordPurchaseQuizScoreFn({ purchaseId, topicId, score, maxScore });
    } catch (err) {
      logClientError({ context: 'recordPurchaseQuizScore', error: err, displayMessage: '', extra: { purchaseId, topicId } });
    }
  }
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

// Self view: the signed-in user's own attempts, most recent first. Allowed
// as a direct client read (firestore.rules: isOwner(uid)) — no callable
// needed. Bounded the same way the school-side callable is, for the same
// reason (a personal history calendar has no need for more).
export async function getMyQuizAttempts() {
  const uid = currentUser()?.uid;
  if (!uid) return [];
  const q = query(
    collection(db, 'users', uid, 'quizAttempts'),
    orderBy('dateKey', 'desc'),
    orderBy('createdAt', 'desc'),
    limit(500),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const a = d.data();
    return {
      id: d.id,
      courseId: a.courseId || null,
      topicId: a.topicId || null,
      courseTitle: a.courseTitle || '',
      topicTitle: a.topicTitle || '',
      score: a.score || 0,
      maxScore: a.maxScore || 0,
      dateKey: a.dateKey || null,
      createdAt: a.createdAt?.toMillis?.() || null,
    };
  });
}

// School owner: one of its actually-linked students' full history —
// { attempts: [...] }, same shape as getMyQuizAttempts's array.
export async function getStudentQuizHistory(studentEmail) {
  const { data } = await getStudentQuizHistoryFn({ studentEmail });
  return data.attempts || [];
}

// School owner: every currently-linked student's quiz history in one list,
// each attempt tagged with studentEmail — backs the school-wide "Scores"
// calendar (src/views/schoolScores.js). See functions/quizScores.js
// getSchoolQuizLog for why this needs no new write path.
export async function getSchoolQuizLog() {
  const { data } = await getSchoolQuizLogFn();
  return data.attempts || [];
}

// Course creator: everyone from a subscribed school who has completed THIS
// quiz, most recent first. Direct client read (firestore.rules: isOwner(uid)
// on users/{uid}/courses/{courseId}/topics/{topicId}/quizTakers) — no
// callable needed, same reasoning as getMyQuizAttempts. Written only by
// functions/schoolMarketplace.js's recordSchoolSubscriptionQuizScore.
export async function getTopicQuizTakers({ courseId, topicId }) {
  const uid = currentUser()?.uid;
  if (!uid || !courseId || !topicId) return [];
  const q = query(
    collection(db, 'users', uid, 'courses', courseId, 'topics', topicId, 'quizTakers'),
    orderBy('createdAt', 'desc'),
    limit(200),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const a = d.data();
    return {
      id: d.id,
      studentUid: a.studentUid || null,
      studentEmail: a.studentEmail || '',
      score: a.score || 0,
      maxScore: a.maxScore || 0,
      dateKey: a.dateKey || null,
      createdAt: a.createdAt?.toMillis?.() || null,
      schoolName: a.schoolName || '',
    };
  });
}
